import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";

import BroadcastersMixin from "@/mixins/http/BroadcastersMixin";
import MeMixin, { GET_PROFILE_DATE } from "@/mixins/http/Agencies/Me";
import ReportExportMixin from "@/mixins/http/CustomReport/ReportExportMixin";

import SingleReport from "@/components/Agencies/ReportBuilder/SingleReport.vue";
import {
  Unit,
  isAuditelUnit,
} from "@/components/Agencies/ReportBuilder/reportUnit";
import {
  AUDITEL_TARGET_REQUIRED_MESSAGE,
  FULL_FORMAT_GRANULARITIES,
  Granularity,
  RAW_FORMAT_GRANULARITIES,
} from "@/components/Agencies/ReportBuilder/granularity";
import DatePicker, { DatePickerData } from "@/components/DatePicker.vue";
import DialogModal from "@/components/DialogModal.vue";
import { BroadcasterSelectData } from "@/components/Report/BroadcasterSelect.vue";
import { SubjectSelectData } from "@/components/Report/SubjectSelect.vue";
import { SlotObject } from "@/components/TimeSlotPicker.vue";
import { VCheckButtonRadioOptions } from "@/components/VCheckButtonRadio.vue";
import { StepItem } from "@/components/VVerticalSteps.vue";
import MultiLevelObject from "@/interfaces/MultiLevelObject";
import QueryableInterface from "@/interfaces/QueryableInterface";
import { AUTOMATIC_REPORTS_ENABLED } from "@/constants";

import {
  ReportCompany,
  ReportSector,
} from "@/mixins/http/CustomReport/ReportProductMixin";
import { AdcontrolProfile, Broadcaster, Target, User } from "@/models";
import MultiSelectBroadcaster, {
  Agency,
} from "@/models/MultiSelectBroadcaster";
import ToastInvoker, { responseErrorMessage } from "@/utils/ToastInvoker";
import { formatApiDate } from "@/services/ApiDateHelper";

export type CustomReportPeriodParameters = {
  starts_at: string | undefined;
  ends_at: string | undefined;
  compare?: boolean;
  compare_starts_at: string | undefined;
  compare_ends_at: string | undefined;
  time_slots_enabled: boolean;
  time_slots: Array<SlotObject>;
};

export type CustomReportWithTemplateParameters =
  CustomReportPeriodParameters & {
    template_id: string;
    granularity?: string | null;
  };

export type CustomReportWithoutPeriodParameters = {
  agencies: Array<Agency>;
  broadcasters: Array<MultiSelectBroadcaster>;
  products: Array<ReportCompany>;
  sectors: Array<ReportSector>;
  output_type?: "full" | "raw";
  unit: Unit;
  special_initiatives_detail?: boolean;
  inclusion: boolean;
  quotas: boolean;
  granularity_enabled: boolean;
  granularity: string | undefined;
  auditel_target: Target | null;
  format_detail: boolean;
  show_vod_devices: boolean;
  apply_client_database: boolean;
  attach_sector_category_class: boolean;
};

export type CustomReportParameters = CustomReportPeriodParameters &
  CustomReportWithoutPeriodParameters;

export enum ReportBuilderTabKey {
  SingleReport = "singleReport",
  AutomaticReport = "automaticReport",
  ReportTemplate = "reportTemplate",
}

// The tab key travels through the URL (`?tab=`), so incoming values must be validated
// before they are assigned to `activeTab`.
export const isReportBuilderTabKey = (
  value: unknown
): value is ReportBuilderTabKey =>
  Object.values(ReportBuilderTabKey).includes(value as ReportBuilderTabKey);

export type ReportBuilderTabItem = {
  id: ReportBuilderTabKey;
  text: string;
  icon: string;
};

export const periodInitialState = (): CustomReportPeriodParameters => ({
  starts_at: undefined,
  ends_at: undefined,
  compare: false,
  compare_starts_at: undefined,
  compare_ends_at: undefined,
  time_slots_enabled: false,
  time_slots: [] as SlotObject[],
});

export const broadcasterInitialState = () => ({
  broadcasters: [] as MultiSelectBroadcaster[],
  agencies: [] as Agency[],
});

export const subjectInitialState = () => ({
  products: [] as ReportCompany[],
  sectors: [] as ReportSector[],
  apply_client_database: false,
  attach_sector_category_class: false,
});

export const outputInitialState = () => ({
  output_type: "full" as const,
  unit: "seconds" as const,
  special_initiatives_detail: false,
  inclusion: false,
  quotas: false,
  granularity_enabled: false,
  granularity: undefined as string | undefined,
  format_detail: false,
  show_vod_devices: false,
  auditel_target: null as Target | null,
});

export const initialParameters = (): CustomReportParameters => ({
  ...periodInitialState(),
  ...broadcasterInitialState(),
  ...subjectInitialState(),
  ...outputInitialState(),
});

export type AllSelectedFlags = {
  broadcasters: boolean;
  agencies: boolean;
  companies: boolean;
  brands: boolean;
  products: boolean;
  sectors: boolean;
  categories: boolean;
  classes: boolean;
};

export const emptyAllSelectedFlags = (): AllSelectedFlags => ({
  broadcasters: false,
  agencies: false,
  companies: false,
  brands: false,
  products: false,
  sectors: false,
  categories: false,
  classes: false,
});

@Component
export default class ReportBuilderMixin extends Mixins(
  ReportExportMixin,
  MeMixin,
  BroadcastersMixin
) {
  @Prop({ required: true }) readonly profile!: AdcontrolProfile;

  @Getter("user") readonly user!: User;

  steps: StepItem[] = [
    { key: "period", label: "Periodo", index: 1 },
    { key: "broadcasters", label: "Emittenti o concessionarie", index: 2 },
    { key: "subject", label: "Oggetto", index: 3 },
    { key: "output", label: "Output", index: 4 },
  ];
  currentStep = "period";
  parameters: CustomReportParameters = initialParameters();
  allSelected: AllSelectedFlags = emptyAllSelectedFlags();
  maxDate = "";

  protected restoringTemplate = false;

  protected get isTemplateFlow(): boolean {
    return false;
  }

  activeTab: ReportBuilderTabKey = ReportBuilderTabKey.SingleReport;
  tabItems: ReportBuilderTabItem[] = [
    {
      id: ReportBuilderTabKey.SingleReport,
      text: "Report Singolo",
      icon: "file-download-02",
    },
    {
      id: ReportBuilderTabKey.AutomaticReport,
      text: "Report Automatico",
      icon: "clock-fast-forward",
    },
    { id: ReportBuilderTabKey.ReportTemplate, text: "Template", icon: "table" },
  ];

  get isSingleReportTab(): boolean {
    return this.activeTab === ReportBuilderTabKey.SingleReport;
  }

  get isTemplateTab(): boolean {
    return this.activeTab === ReportBuilderTabKey.ReportTemplate;
  }

  get isAutomaticTab(): boolean {
    return this.activeTab === ReportBuilderTabKey.AutomaticReport;
  }

  // AutomaticReport is hidden until the pending ReportTemplate features land.
  // See src/constants.ts.
  get visibleTabItems(): ReportBuilderTabItem[] {
    if (AUTOMATIC_REPORTS_ENABLED) return this.tabItems;
    return this.tabItems.filter(
      (item) => item.id !== ReportBuilderTabKey.AutomaticReport
    );
  }

  selectedBroadcasters: Broadcaster[] = [];
  selectedBroadcastersLoading = false;
  generatingReport = false;

  get singleReport(): SingleReport | undefined {
    return this.$refs.singleReport as SingleReport | undefined;
  }

  get periodDatePicker(): DatePicker | undefined {
    return this.singleReport?.periodDatePicker;
  }

  get compareDatePicker(): DatePicker | undefined {
    return this.singleReport?.compareDatePicker;
  }

  get successModal(): DialogModal | undefined {
    return this.$refs.successModal as DialogModal | undefined;
  }

  async created() {
    this.resetPeriodStep();
    this.maxDate = await this.loadProfileDate();
    this.initializeDefaultDates();
  }

  switchTab(id: string) {
    if (isReportBuilderTabKey(id)) {
      this.activeTab = id;
    }
  }

  initializeDefaultDates(): void {
    if (!this.maxDate) return;
    this.parameters.ends_at = this.maxDate;
    this.parameters.starts_at = this.$moment(this.maxDate)
      .subtract(6, "days")
      .format("YYYY-MM-DD");
    this.periodDatePicker?.forceDateSelection(
      this.parameters.starts_at,
      this.parameters.ends_at
    );
  }

  async loadProfileDate(): Promise<string> {
    const date = await this.getProfileLastDate(
      this.profile.slug,
      GET_PROFILE_DATE
    );
    if (!date.meta.date) {
      return "";
    }
    return formatApiDate(date.meta.date, "YYYY-MM-DD");
  }

  async doExport() {
    if (this.generatingReport) return;

    if (this.unitSelectedIsAuditel && this.parameters.auditel_target == null) {
      ToastInvoker.errorToast(AUDITEL_TARGET_REQUIRED_MESSAGE, null);
      return;
    }

    this.generatingReport = true;
    try {
      await this.postExport(this.parameters);
      await this.successModal?.dialog();
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
    } finally {
      this.generatingReport = false;
    }
  }

  @Watch("outputType")
  onOutputTypeUpdated(val: string) {
    if (this.restoringTemplate) return;
    if (val === "raw") {
      this.parameters.inclusion = false;
      this.parameters.quotas = false;
    } else {
      this.parameters.format_detail = false;
    }
    this.parameters.granularity_enabled = false;
    this.parameters.show_vod_devices = false;
  }

  @Watch("parameters.unit")
  onUnitChanged() {
    if (this.restoringTemplate) return;
    if (!this.unitSelectedIsAuditel) this.parameters.auditel_target = null;
  }

  @Watch("compareEnabled")
  onCompareUpdated(val: boolean) {
    if (!val) {
      this.parameters.compare_ends_at = undefined;
      this.parameters.compare_starts_at = undefined;
      this.compareDatePicker?.reset();
    }
  }

  @Watch("timeSlotsEnabled")
  onTimeSlotsEnabledUpdated(val: boolean) {
    if (!val) {
      this.parameters.time_slots = [];
    }
  }

  @Watch("specialInitiativesEnabled")
  onSpecialInitiativesUpdated(val: boolean) {
    if (this.restoringTemplate) return;
    if (!val) {
      this.parameters.granularity = undefined;
      this.parameters.granularity_enabled = false;
    }
  }

  onTimeSlotsInput(time_slots: SlotObject[]) {
    this.parameters.time_slots = time_slots;
  }

  onOutputTypeSelected() {
    if (
      this.parameters.output_type === "raw" ||
      this.parameters.unit !== "spots_and_seconds"
    )
      return;

    this.parameters.unit = this.unitSelectOptions[0]
      .id as CustomReportParameters["unit"];
  }

  get unitSelectOptions() {
    let auditel_options: Array<MultiLevelObject> = [];
    let rawOptions: Array<MultiLevelObject> = [];

    if (this.auditelEnabled) {
      auditel_options = [
        { id: "grp", text: "GRP" },
        { id: "audience", text: "Contatti" },
      ];
    }

    if (this.outputType === "raw") {
      rawOptions = [{ id: "spots_and_seconds", text: "N° Spot e N° Secondi" }];
    }

    return [
      { id: "spots", text: "N° Spot", selected: true },
      { id: "seconds", text: "N° Secondi" },
      ...rawOptions,
      ...auditel_options,
    ];
  }

  get unitSelectedIsAuditel(): boolean {
    return isAuditelUnit(this.parameters.unit);
  }

  get specialInitiativesEnabled(): boolean {
    return this.parameters.special_initiatives_detail ?? false;
  }

  get compareEnabled(): boolean {
    return this.parameters.compare ?? false;
  }

  get compareStartsAt(): string | undefined {
    return this.parameters.compare_starts_at;
  }

  get compareEndsAt(): string | undefined {
    return this.parameters.compare_ends_at;
  }

  get startsAt(): string | undefined {
    return this.parameters.starts_at;
  }

  get endsAt(): string | undefined {
    return this.parameters.ends_at;
  }

  get outputType(): "full" | "raw" | undefined {
    return this.parameters.output_type;
  }

  get timeSlotsEnabled(): boolean {
    return this.parameters.time_slots_enabled ?? false;
  }

  get timeSlots(): SlotObject[] | undefined {
    return this.parameters.time_slots;
  }

  get agenciesDisabled() {
    return (this.parameters.broadcasters?.length ?? 0) > 0;
  }

  get broadcastersDisabled() {
    return (this.parameters.agencies?.length ?? 0) > 0;
  }

  get productsDisabled() {
    return (this.parameters.sectors?.length ?? 0) > 0;
  }

  get sectorsDisabled() {
    return (this.parameters.products?.length ?? 0) > 0;
  }

  get step(): StepItem | undefined {
    return this.steps.find((s) => s.key === this.currentStep);
  }

  get outputTypeOptions(): VCheckButtonRadioOptions {
    return {
      full: "Completo",
      raw: "RAW Data",
    };
  }

  get auditelEnabled(): boolean {
    return (
      this.mixinProfile.accountType === "tv" && this.user.hasFeature("auditel")
    );
  }

  get granularityEnabled(): boolean {
    return this.parameters.granularity_enabled ?? false;
  }

  get isVodOnly(): boolean {
    return this.profile?.mediaSourceKind === "msk_vod";
  }

  reset() {
    this.parameters = initialParameters();
    this.allSelected = emptyAllSelectedFlags();
    this.currentStep = "period";
    this.compareDatePicker?.reset();
    this.periodDatePicker?.reset();
    this.resetSelectedBroadcasters();
    this.initializeDefaultDates();
  }

  onPeriodDateSelected(data: DatePickerData) {
    this.parameters.starts_at = data.range?.from?.toString();
    this.parameters.ends_at = data.range?.to?.toString();
  }

  onComparePeriodDateSelected(data: DatePickerData) {
    this.parameters.compare_starts_at = data.range?.from?.toString();
    this.parameters.compare_ends_at = data.range?.to?.toString();
  }

  onBroadcastersSelected(data: BroadcasterSelectData) {
    this.parameters.broadcasters = data.broadcasters;
    this.parameters.agencies = data.agencies;
    this.allSelected.broadcasters = data.allBroadcastersSelected ?? false;
    this.allSelected.agencies = data.allAgenciesSelected ?? false;
  }

  async loadSelectedBroadcasters(
    selectedBroadcasters: MultiSelectBroadcaster[],
    selectedAgencies: Agency[]
  ) {
    this.selectedBroadcasters = [];

    if (!this.profile.canAccessVod) return;

    if (!selectedBroadcasters.length && !selectedAgencies.length) return;

    const broadcastersQuery: QueryableInterface = {
      filter: {
        ids: selectedBroadcasters
          .map((broadcaster) => broadcaster.id)
          .filter((id) => !!id) as number[],
        agencies: selectedAgencies.map((agency) => agency.id),
      },
      page: { size: "all" },
    };

    try {
      this.selectedBroadcastersLoading = true;
      this.selectedBroadcasters = await this.getBroadcasters(broadcastersQuery);
    } catch (e) {
      this.selectedBroadcasters = [];
    } finally {
      this.selectedBroadcastersLoading = false;
    }

    if (!this.selectedBroadcasters.some((broadcaster) => broadcaster.vod)) {
      this.parameters.show_vod_devices = false;
    }
  }

  resetSelectedBroadcasters(): void {
    this.selectedBroadcasters = [];
    this.parameters.show_vod_devices = false;
  }

  onSubjectsSelected(data: SubjectSelectData) {
    this.parameters.apply_client_database = data.applyClientDatabase;
    this.parameters.attach_sector_category_class =
      data.attachSectorCategoryClass;
    if (this.parameters.apply_client_database) {
      if (data.products.length > 0) {
        this.parameters.products = [];
        this.parameters.sectors = [];
      }
    } else {
      this.parameters.products = data.products;
      this.parameters.sectors = data.sectors;
    }

    const allProductTypes = data.allProductTypes ?? [];
    const allSectorTypes = data.allSectorTypes ?? [];
    this.allSelected.companies = allProductTypes.includes("company");
    this.allSelected.brands = allProductTypes.includes("brand");
    this.allSelected.products = allProductTypes.includes("product");
    this.allSelected.sectors = allSectorTypes.includes("sector");
    this.allSelected.categories = allSectorTypes.includes("category");
    this.allSelected.classes = allSectorTypes.includes("class");
  }

  onStep(step: string) {
    const fromIndex = this.steps.findIndex((s) => s.key === this.currentStep);
    const toIndex = this.steps.findIndex((s) => s.key === step);
    this.currentStep = step;
    if (this.restoringTemplate) return;

    if (step === "output") {
      this.loadSelectedBroadcasters(
        this.parameters.broadcasters || [],
        this.parameters.agencies || []
      );
      return;
    }

    if (toIndex >= fromIndex) return;

    if (this.isTemplateFlow) return;

    switch (step) {
      case "period":
        this.resetBroadcasterStep();
        this.resetSubjectStep();
        this.resetOutputStep();
        break;
      case "broadcasters":
        this.resetSubjectStep();
        this.resetOutputStep();
        break;
      case "subject":
        this.resetSelectedBroadcasters();
        this.resetOutputStep();
        break;
    }
  }

  resetPeriodStep(): void {
    this.parameters = { ...this.parameters, ...periodInitialState() };
  }

  resetBroadcasterStep(): void {
    this.parameters = { ...this.parameters, ...broadcasterInitialState() };
    this.allSelected.broadcasters = false;
    this.allSelected.agencies = false;
  }

  resetSubjectStep(): void {
    this.parameters = { ...this.parameters, ...subjectInitialState() };
    this.allSelected.companies = false;
    this.allSelected.brands = false;
    this.allSelected.products = false;
    this.allSelected.sectors = false;
    this.allSelected.categories = false;
    this.allSelected.classes = false;
  }

  resetOutputStep(): void {
    this.parameters = { ...this.parameters, ...outputInitialState() };
  }

  onForward() {
    const currentStepIndex = this.steps.findIndex(
      (s) => s.key === this.currentStep
    );
    if (currentStepIndex + 1 < this.steps.length) {
      this.currentStep = this.steps[currentStepIndex + 1].key;
    }
  }

  async onTargetSelected(target: Target) {
    this.parameters.auditel_target = target;
  }

  get grpTargetId(): string | null {
    if (
      this.parameters.auditel_target &&
      this.mixinProfile.accountType === "tv"
    )
      return this.parameters.auditel_target?.id;
    else return null;
  }

  isCurrentStep(step: string): boolean {
    return this.currentStep == step;
  }

  getSelectedDays(): number {
    return Math.abs(
      this.$moment(this.parameters.ends_at).diff(
        this.$moment(this.parameters.starts_at),
        "days"
      )
    );
  }

  granularityOptionsForDays(days: number, raw: boolean): Granularity[] {
    const source = raw ? RAW_FORMAT_GRANULARITIES : FULL_FORMAT_GRANULARITIES;

    return source.filter((e) =>
      e.maxDays ? days >= e.minDays && days <= e.maxDays : days >= e.minDays
    );
  }

  get granularities(): Granularity[] {
    const raw = this.outputType === "raw";

    if (this.isTemplateFlow) {
      return raw ? RAW_FORMAT_GRANULARITIES : FULL_FORMAT_GRANULARITIES;
    }

    return this.granularityOptionsForDays(this.getSelectedDays(), raw);
  }

  get timeSlotsValid(): boolean {
    if (!this.parameters.time_slots_enabled) {
      return true;
    } else {
      return (this.parameters.time_slots?.length ?? 0) > 0;
    }
  }

  get compareValid(): boolean {
    if (!this.parameters.compare) {
      return true;
    } else {
      return (this.parameters.compare_starts_at?.length ?? 0) > 0;
    }
  }

  get isBroadcastersStepEnabled(): boolean {
    if (!this.compareValid) {
      return false;
    }

    if (!this.timeSlotsValid) {
      return false;
    }

    if ((this.parameters.starts_at?.length ?? 0) == 0) {
      return false;
    }
    return true;
  }

  get subjectsStepEnabled(): boolean {
    return (
      (this.parameters.agencies?.length ?? 0) > 0 ||
      (this.parameters.broadcasters?.length ?? 0) > 0
    );
  }

  get outputStepEnabled(): boolean {
    return (
      (this.parameters.products?.length ?? 0) > 0 ||
      (this.parameters.sectors?.length ?? 0) > 0 ||
      (this.parameters.apply_client_database ?? false)
    );
  }

  get exportButtonEnabled(): boolean {
    return this.parameters.granularity_enabled
      ? this.parameters.granularity != undefined
      : true;
  }

  get showVodDevicesDisabled(): boolean {
    return (
      this.selectedBroadcasters.some((broadcaster) => broadcaster.vod) ==
        false ||
      ((this.parameters.granularity_enabled ?? false) &&
        this.parameters.output_type !== "raw")
    );
  }
}
